import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { Spin } from 'antd';
import { ProtectedRoute } from './ProtectedRoute';

// ============ Layouts ============
import { v2Routes } from '@/v2/routes/v2Routes';
import { buildAdminUsersRedirectPath, type AdminUsersRedirectMode } from '@/v2/routes/adminUsersRedirect';
import { V2_ROOT_ROUTE_ROLES } from '@/v2/routes/criticalRoleRoutePolicy';
const V2Layout = lazy(() => import('@/v2/app/V2Layout').then(m => ({ default: m.V2Layout })));

// ============ Auth Pages ============
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPartnerPage = lazy(() => import('@/pages/auth/RegisterPartnerPage').then(m => ({ default: m.RegisterPartnerPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const EmployeeInviteAcceptPage = lazy(() => import('@/pages/auth/EmployeeInviteAcceptPage').then(m => ({ default: m.EmployeeInviteAcceptPage })));

// ============ Error Pages ============
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const ServerErrorPage = lazy(() => import('@/pages/errors/ServerErrorPage'));

// ============ Public Pages ============
const PublicProofPage = lazy(() => import('@/pages/public/PublicProofPage').then(m => ({ default: m.PublicProofPage })));
// FLOW[44024]: B3 — публичная страница КП по токен-ссылке (K-2.2).
const PublicQuotePage = lazy(() => import('@/pages/public/PublicQuotePage').then(m => ({ default: m.PublicQuotePage })));
const PublicDesignTaskPage = lazy(() => import('@/pages/public/PublicDesignTaskPage').then(m => ({ default: m.PublicDesignTaskPage })));
const PublicClientOpsPage = lazy(() => import('@/pages/public/PublicClientOpsPage').then(m => ({ default: m.PublicClientOpsPage })));
const PublicPrepressLandingPage = lazy(() => import('@/pages/public/PublicPrepressLandingPage').then(m => ({ default: m.PublicPrepressLandingPage })));
const PublicPrepressJobsPage = lazy(() => import('@/pages/public/PublicPrepressJobsPage').then(m => ({ default: m.PublicPrepressJobsPage })));
const PublicPrepressJobPage = lazy(() => import('@/pages/public/PublicPrepressJobPage').then(m => ({ default: m.PublicPrepressJobPage })));
const PublicPrepressTelegramMiniAppPage = lazy(() => import('@/pages/public/PublicPrepressTelegramMiniAppPage').then(m => ({ default: m.PublicPrepressTelegramMiniAppPage })));
const V3PrepressPage = lazy(() => import('@/pages/public/v3').then(m => ({ default: m.V3PrepressPage })));
const V3PrepressJobPage = lazy(() => import('@/pages/public/v3').then(m => ({ default: m.V3PrepressJobPage })));
const V3PrepressBatchResults = lazy(() => import('@/pages/public/v3').then(m => ({ default: m.V3PrepressBatchResults })));
const WowMockupPage = lazy(() => import('@/pages/public/WowMockupPage').then(m => ({ default: m.WowMockupPage })));
const PublicDroneTrackPage = lazy(() => import('@/pages/public/PublicDroneTrackPage').then(m => ({ default: m.PublicDroneTrackPage })));
const V2ManagerMiniAppPage = lazy(() => import('@/v2/pages/V2ManagerMiniAppPage').then(m => ({ default: m.V2ManagerMiniAppPage })));

// ============ Partner Invite ============
const PartnerInviteAcceptPage = lazy(() => import('@/pages/partner/PartnerInviteAcceptPage').then(m => ({ default: m.PartnerInviteAcceptPage })));

// ============ Loading Component ============
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  }}>
    <Spin size="large" />
  </div>
);

interface LegacyRedirectProps {
  to: string;
  preserveSearch?: boolean;
}

const LegacyRedirect = ({ to, preserveSearch = true }: LegacyRedirectProps) => {
  const location = useLocation();
  const search = preserveSearch ? location.search : '';
  return <Navigate to={`${to}${search}`} replace />;
};

interface LegacyParamRedirectProps {
  to: string;
}

const LegacyParamRedirect = ({ to }: LegacyParamRedirectProps) => {
  const params = useParams();
  const location = useLocation();
  const target = Object.entries(params).reduce((path, [key, value]) => {
    if (!value) {
      return path;
    }
    return path.replace(`:${key}`, value);
  }, to);
  return <Navigate to={`${target}${location.search}`} replace />;
};

const LegacySalesRedirect = () => {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get('tab');

  if (tab === 'orders') {
    return <Navigate to="/v2/orders" replace />;
  }
  if (tab === 'clients') {
    return <Navigate to="/v2/clients" replace />;
  }
  if (tab === 'leads') {
    return <Navigate to="/v2/pipeline?board=leads" replace />;
  }
  if (tab === 'pipeline') {
    return <Navigate to="/v2/pipeline" replace />;
  }
  if (tab === 'analytics') {
    return <Navigate to="/v2/dashboard" replace />;
  }
  return <Navigate to="/v2/dashboard" replace />;
};

const LegacyAdminUsersRedirect = ({ mode }: { mode: AdminUsersRedirectMode }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={buildAdminUsersRedirectPath(mode, id, location.search)} replace />;
};

export const router = createBrowserRouter([
  // ============ Public Routes ============
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterPartnerPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/v2/invite',
    element: (
      <Suspense fallback={<PageLoader />}>
        <EmployeeInviteAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/proof/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicProofPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/drone/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicDroneTrackPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/quote/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicQuotePage />
      </Suspense>
    ),
  },
  {
    path: '/portal/design/:token',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicDesignTaskPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/chat',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicClientOpsPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicPrepressLandingPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/tma',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicPrepressTelegramMiniAppPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/jobs',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicPrepressJobsPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/jobs/:jobId',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicPrepressJobPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/new',
    element: (
      <Suspense fallback={<PageLoader />}>
        <V3PrepressPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/new/jobs/:jobId',
    element: (
      <Suspense fallback={<PageLoader />}>
        <V3PrepressJobPage />
      </Suspense>
    ),
  },
  {
    path: '/portal/prepress/new/batch-results',
    element: (
      <Suspense fallback={<PageLoader />}>
        <V3PrepressBatchResults />
      </Suspense>
    ),
  },
  {
    path: '/wow',
    element: (
      <Suspense fallback={<PageLoader />}>
        <WowMockupPage />
      </Suspense>
    ),
  },
  {
    // FLOW[50338]: compact ERP-authenticated shell; channel initData exchange
    // stays fail-closed until the common Telegram/MAX personal binding exists.
    path: '/miniapp/manager/*',
    element: (
      <ProtectedRoute
        allowedRoles={['admin', 'manager', 'sales_manager', 'accountant', 'executive']}
      >
        <Suspense fallback={<PageLoader />}>
          <V2ManagerMiniAppPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/v2',
    element: (
      <ProtectedRoute allowedRoles={[...V2_ROOT_ROUTE_ROLES]}>
        <Suspense fallback={<PageLoader />}>
          <V2Layout />
        </Suspense>
      </ProtectedRoute>
    ),
    children: v2Routes,
  },
  {
    path: '/legacy/*',
    element: (
      <ProtectedRoute>
        <LegacyRedirect to="/v2/dashboard" preserveSearch={false} />
      </ProtectedRoute>
    ),
  },

  // ============ Legacy Redirects (v1 -> v2) ============
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    ),
    errorElement: (
      <Suspense fallback={<PageLoader />}>
        <ServerErrorPage />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/v2/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <Navigate to="/v2/dashboard" replace />,
      },

      // Sales legacy paths
      {
        path: 'sales',
        element: <LegacySalesRedirect />,
      },
      {
        path: 'sales/orders',
        element: <LegacyRedirect to="/v2/orders" />,
      },
      {
        path: 'sales/orders/new',
        element: <LegacyRedirect to="/v2/orders/new" />,
      },
      {
        path: 'sales/orders/:id',
        element: <LegacyParamRedirect to="/v2/orders/:id" />,
      },
      {
        path: 'sales/orders/:id/edit',
        element: <LegacyParamRedirect to="/v2/orders/:id/edit" />,
      },
      {
        path: 'sales/clients',
        element: <LegacyRedirect to="/v2/clients" />,
      },
      {
        path: 'sales/clients/new',
        element: <LegacyRedirect to="/v2/clients/new" />,
      },
      {
        path: 'sales/clients/:id',
        element: <LegacyParamRedirect to="/v2/clients/:id" />,
      },
      {
        path: 'sales/clients/:id/edit',
        element: <LegacyParamRedirect to="/v2/clients/:id/edit" />,
      },
      {
        path: 'sales/crm/client/:id',
        element: <LegacyParamRedirect to="/v2/clients/:id" />,
      },
      {
        path: 'sales/leads',
        element: <LegacyRedirect to="/v2/pipeline?board=leads" preserveSearch={false} />,
      },
      {
        path: 'sales/pipeline',
        element: <LegacyRedirect to="/v2/pipeline" />,
      },
      {
        path: 'sales/analytics',
        element: <LegacyRedirect to="/v2/dashboard" preserveSearch={false} />,
      },

      // Production legacy paths
      {
        path: 'production',
        element: <LegacyRedirect to="/v2/production" />,
      },
      {
        path: 'production/orders/:id',
        element: <LegacyParamRedirect to="/v2/orders/:id" />,
      },

      // Logistics legacy paths
      {
        path: 'logistics',
        element: <LegacyRedirect to="/v2/logistics" />,
      },

      // Media library legacy paths
      {
        path: 'media',
        element: <LegacyRedirect to="/v2/media" />,
      },

      // Design tasks legacy paths
      {
        path: 'design-tasks',
        element: <LegacyRedirect to="/v2/design-tasks" />,
      },

      // Stock/Warehouse legacy paths
      {
        path: 'stock',
        element: <LegacyRedirect to="/v2/stock" />,
      },
      {
        path: 'stock/locations',
        element: <LegacyRedirect to="/v2/stock" />,
      },
      {
        path: 'stock/materials',
        element: <LegacyRedirect to="/v2/stock" />,
      },
      {
        path: 'stock/materials/new',
        element: <LegacyRedirect to="/v2/stock" />,
      },
      {
        path: 'stock/materials/:id',
        element: <LegacyRedirect to="/v2/stock" />,
      },
      {
        path: 'stock/materials/:id/edit',
        element: <LegacyRedirect to="/v2/stock" />,
      },

      // Accounting legacy paths
      {
        path: 'accounting',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/reports',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/debts',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/documents',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/writeoffs',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/taxes',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/vat',
        element: <LegacyRedirect to="/v2/accounting" />,
      },
      {
        path: 'accounting/orders/:id',
        element: <LegacyParamRedirect to="/v2/orders/:id" />,
      },

      // AI visualization legacy path
      {
        path: 'ai-visualization',
        element: <LegacyRedirect to="/v2/dashboard" preserveSearch={false} />,
      },

      // Executive legacy paths
      {
        path: 'executive',
        element: <LegacyRedirect to="/v2/executive" />,
      },
      {
        path: 'executive/orders/:id',
        element: <LegacyParamRedirect to="/v2/orders/:id" />,
      },

      // Partner invite acceptance
      {
        path: 'partner/invite',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PartnerInviteAcceptPage />
          </Suspense>
        ),
      },

      // Partner legacy paths
      {
        path: 'partner/orders',
        element: <LegacyRedirect to="/v2/orders" />,
      },
      {
        path: 'partner/orders/new',
        element: <LegacyRedirect to="/v2/orders/new" />,
      },
      {
        path: 'partner/orders/:id',
        element: <LegacyParamRedirect to="/v2/orders/:id" />,
      },
      {
        path: 'partner',
        element: <LegacyRedirect to="/v2/dashboard" />,
      },

      // Admin legacy paths → ведут напрямую в секции единого центра настроек
      // (без промежуточного /v2/admin: он тоже редирект, а bare-цель теряла tab-секцию).
      {
        path: 'admin',
        element: <LegacyRedirect to="/v2/settings" />,
      },
      {
        path: 'admin/users',
        element: <LegacyAdminUsersRedirect mode="list" />,
      },
      {
        path: 'admin/users/new',
        element: <LegacyAdminUsersRedirect mode="new" />,
      },
      {
        path: 'admin/users/:id/edit',
        element: <LegacyAdminUsersRedirect mode="edit" />,
      },
      {
        path: 'admin/partners',
        element: <LegacyRedirect to="/v2/settings/partners" />,
      },
      {
        path: 'admin/roles',
        element: <LegacyRedirect to="/v2/settings/roles" />,
      },
      {
        path: 'admin/sessions',
        element: <LegacyRedirect to="/v2/settings/sessions" />,
      },
      {
        path: 'admin/api-keys',
        element: <LegacyRedirect to="/v2/settings/api-keys" />,
      },
      {
        path: 'admin/legal-entities',
        element: <LegacyRedirect to="/v2/settings/legal-entities" />,
      },
      {
        path: 'admin/salary-settings',
        element: <LegacyRedirect to="/v2/settings/salary" />,
      },
      {
        path: 'admin/settings',
        element: <LegacyRedirect to="/v2/settings" />,
      },
      {
        path: 'admin/suppliers',
        // FLOW[45554]: PRW-W5 — поставщики живут в каталоге (таб «Поставщики»);
        // старый редирект вёл на /v2/admin, где раздела нет (тупик из аудита W0).
        // preserveSearch=false: to уже содержит query, конкатенация дала бы битый URL
        element: <LegacyRedirect to="/v2/catalog?tab=suppliers" preserveSearch={false} />,
      },

      // Catalog legacy paths
      {
        path: 'catalog',
        element: <LegacyRedirect to="/v2/catalog" />,
      },
      {
        path: 'catalog/product/:id',
        element: <LegacyParamRedirect to="/v2/catalog/product/:id" />,
      },
      {
        path: 'catalog/registry',
        element: <LegacyRedirect to="/v2/catalog/registry" />,
      },

      // Common legacy paths
      {
        path: 'profile',
        element: <LegacyRedirect to="/v2/profile" />,
      },
      {
        path: 'notifications',
        element: <LegacyRedirect to="/v2/notifications" />,
      },
      {
        path: 'settings',
        element: <LegacyRedirect to="/v2/settings" />,
      },
    ],
  },

  // ============ Error Routes ============
  {
    path: '/error',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ServerErrorPage />
      </Suspense>
    ),
  },
  {
    path: '/404',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export { roleRoutes } from './roleRoutes';
// FLOW[27004]: MS229 - Export permission hooks for use in components
export { useHasPermission, useHasAnyPermission } from './permissionHooks';
